import React, { useContext, useState, useEffect } from 'react';
import AuthContext from '../contexts/AuthContext';
import api from '../api';

function Home() {
  const { user } = useContext(AuthContext);
  const [language, setLanguage] = useState('en');
  const [isMounted, setIsMounted] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);
  const [notifications, setNotifications] = useState([]);

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language');
    if (savedLanguage) {
      setLanguage(savedLanguage);
    } else {
      setLanguage('en');
    }
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (isMounted) {
      localStorage.setItem('language', language);
    }
  }, [language, isMounted]);

  useEffect(() => {
    const fetchHomeData = async () => {
      try {
        const [leaderboardResponse, notificationsResponse] = await Promise.all([
          api.get('/tests/leaderboard'),
          api.get('/notifications')
        ]);

        setLeaderboard(leaderboardResponse.data.leaderboard || []);
        setNotifications((notificationsResponse.data || []).slice(0, 5));
      } catch (error) {
        console.error('Failed to fetch home page data:', error);
      }
    };

    fetchHomeData();
  }, []);

  const getRankStyle = (rank) => {
    if (rank === 1) return { background: 'linear-gradient(135deg, #f6d365, #fda085)', color: '#5a3d00' };
    if (rank === 2) return { background: 'linear-gradient(135deg, #dfe6e9, #b2bec3)', color: '#2d3436' };
    if (rank === 3) return { background: 'linear-gradient(135deg, #f7c59f, #d88c5a)', color: '#4a2d20' };
    return { background: '#f8f9fa', color: '#2c3e50' };
  };

  const getRankLabel = (rank) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return `#${rank}`;
  };

  if (!isMounted) return null;

  let expiryDate = user?.subscriptionExpiry ? new Date(user.subscriptionExpiry) : null;
  let daysLeft = expiryDate ? Math.ceil((expiryDate - new Date()) / (1000 * 60 * 60 * 24)) : null;

  return (
    <div style={{
      padding: '0.5rem 1rem',
      maxWidth: '1200px',
      margin: '0 auto',
      backgroundColor: '#f8f9fa',
      minHeight: '100vh'
    }}>

      <div style={{
        backgroundColor: 'white',
        padding: '1.5rem',
        borderRadius: '20px',
        boxShadow: '0 12px 40px rgba(0,0,0,0.1)',
        border: '1px solid #e9ecef',
        marginTop: '2rem',
        textAlign: 'center'
      }}
        onMouseEnter={(e) => {
          e.target.style.boxShadow = '0 16px 48px rgba(0,0,0,0.15)';
        }}
        onMouseLeave={(e) => {
          e.target.style.boxShadow = '0 12px 40px rgba(0,0,0,0.1)';
        }}
      >
        <div
          style={{
            position: 'relative',
            marginBottom: '2rem',
            paddingRight: '1rem',
          }}
        >
          {/* Centered Heading */}
          <h2
            style={{
              fontSize: '32px',
              margin: 0,
              color: '#2c3e50',
              borderBottom: '3px solid #667eea',
              paddingBottom: '1rem',
              fontFamily:
                language === 'hi' ? 'KrutiDev_010, serif' : 'Arial, sans-serif',
              textAlign: 'center',
            }}
          >
            {language === 'hi' ? 'संपर्क करें' : 'Stay Connected'}
          </h2>

          {/* Language Toggle - Right */}
          <div
            style={{
              position: 'absolute',
              right: '1rem',
              top: '50%',
              transform: 'translateY(-50%)',
              display: 'flex',
              backgroundColor: '#e9ecef',
              borderRadius: '25px',
              padding: '0.5rem',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            }}
          >
            <button
              onClick={() => setLanguage('en')}
              style={{
                backgroundColor: language === 'en' ? '#28a745' : 'transparent',
                color: language === 'en' ? 'white' : '#6c757d',
                border: 'none',
                padding: '0.5rem 1rem',
                borderRadius: '20px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold',
                transition: 'all 0.3s ease',
              }}
            >
              English
            </button>

            <button
              onClick={() => setLanguage('hi')}
              style={{
                backgroundColor: language === 'hi' ? '#6c757d' : 'transparent',
                color: language === 'hi' ? 'white' : '#6c757d',
                border: 'none',
                padding: '0.5rem 1rem',
                borderRadius: '20px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold',
                transition: 'all 0.3s ease',
              }}
            >
              हिंदी
            </button>
          </div>
        </div>

        <div style={{
          display: 'flex',
          justifyContent: 'center',
          gap: '3rem',
          flexWrap: 'wrap',
          marginBottom: '0.75rem'
        }}>
          <div style={{
            textAlign: 'center',
            padding: '0.5rem',
            backgroundColor: '#f8f9fa',
            borderRadius: '15px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            transition: 'transform 0.3s ease'
          }}
            onMouseEnter={(e) => e.target.style.transform = 'translateY(-5px)'}
            onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
          >
            <a
              href="https://www.youtube.com/channel/UCM9d66lvKNV0Q4BMXKhdpwQ"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                textDecoration: 'none',
                color: '#2c3e50',
                fontSize: '18px',
                fontWeight: 'bold'
              }}
            >
              📺 YouTube Channel
            </a>
            <p style={{ marginTop: '0.5rem', fontSize: '14px', color: '#6c757d' }}>
              {language === 'hi' ? 'हमारे यूट्यूब चैनल को सब्सक्राइब करें' : 'Subscribe to our YouTube Channel'}
            </p>
          </div>

          <div style={{
            textAlign: 'center',
            padding: '1.5rem',
            backgroundColor: '#f8f9fa',
            borderRadius: '15px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
            transition: 'transform 0.3s ease'
          }}
            onMouseEnter={(e) => e.target.style.transform = 'translateY(-5px)'}
            onMouseLeave={(e) => e.target.style.transform = 'translateY(0)'}
          >
            <button
              onClick={() => setShowQRModal(true)}
              style={{
                background: 'none',
                border: 'none',
                color: '#2c3e50',
                fontSize: '18px',
                fontWeight: 'bold',
                cursor: 'pointer',
                textDecoration: 'none'
              }}
            >
              💬 Ask Doubts
            </button>
            <p style={{ marginTop: '0.5rem', fontSize: '14px', color: '#6c757d' }}>
              {language === 'hi' ? 'ग्रुप जॉइन करने के लिए QR कोड स्कैन करें' : 'Scan QR Code to Join Group'}
            </p>
          </div>
        </div>
      </div>

      {showQRModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 2000
        }}
          onClick={() => setShowQRModal(false)}
        >
          <div style={{
            backgroundColor: 'white',
            padding: '2rem',
            borderRadius: '15px',
            boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
            textAlign: 'center',
            maxWidth: '400px',
            width: '90%'
          }}>
            <h3 style={{ marginBottom: '1rem', color: '#2c3e50' }}>
              {language === 'hi' ? 'व्हाट्सएप ग्रुप जॉइन करें' : 'Join WhatsApp Group'}
            </h3>
            <p style={{ marginBottom: '1.5rem', fontSize: '16px' }}>
              {language === 'hi' ? 'नीचे दिए गए QR कोड को स्कैन करें' : 'Scan the QR code below to join'}
            </p>
            <img
              src="/qr_code.jpeg"
              alt="WhatsApp Group QR Code"
              style={{
                width: '250px',
                height: '400px',
                marginBottom: '1rem'
              }}
            />
            <p style={{ fontSize: '14px', color: '#6c757d' }}>
              {language === 'hi' ? 'या सीधे लिंक से जॉइन करें: ' : 'Or join directly via link: '}
              <a
                href="https://wa.me/message/CO7FWZPDZCSHH1"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#25D366' }}
              >
                WhatsApp Link
              </a>
            </p>
            <button
              onClick={() => setShowQRModal(false)}
              style={{
                backgroundColor: '#dc3545',
                color: 'white',
                border: 'none',
                padding: '0.75rem 1.5rem',
                borderRadius: '8px',
                cursor: 'pointer',
                marginTop: '1rem'
              }}
            >
              {language === 'hi' ? 'बंद करें' : 'Close'}
            </button>
          </div>
        </div>
      )}

      <div style={{
        display: 'flex',
        gap: '1rem',
        marginBottom: '1.5rem',
        flexWrap: 'wrap',
        alignItems: 'stretch'
      }}>
        <div style={{
          flex: '1.4',
          minWidth: '280px',
          backgroundColor: 'white',
          borderRadius: '16px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
          border: '1px solid #e9ecef',
          overflow: 'hidden'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '0.85rem 1.1rem',
            background: 'linear-gradient(135deg, #f8c537, #f0932b)',
            color: '#2c3e50'
          }}>
            <h3 style={{ margin: 0, fontSize: '22px' }}>
              {language === 'hi' ? 'टॉप 3 रैंकिंग' : 'Top 3 Ranking'}
            </h3>
            <span style={{ fontSize: '12px', fontWeight: 'bold', opacity: 0.9 }}>
              {language === 'hi' ? 'औसत सटीकता' : 'Average Accuracy'}
            </span>
          </div>

          <div style={{ padding: '0.7rem 0.8rem 0.8rem' }}>
            {leaderboard.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#6c757d', padding: '0.75rem 0', fontSize: '14px' }}>
                {language === 'hi' ? 'अभी कोई रैंक उपलब्ध नहीं है।' : 'No rankings available yet.'}
              </div>
            ) : (
              <div style={{ display: 'grid', gap: '0.5rem' }}>
                {leaderboard.map((item, index) => (
                  <div
                    key={item.id || index}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '0.55rem 0.75rem',
                      borderRadius: '10px',
                      border: '1px solid #edf2f7',
                      ...getRankStyle(index + 1)
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                      <div style={{
                        width: '30px',
                        height: '30px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold',
                        fontSize: '0.8rem',
                        background: index < 3 ? 'rgba(255,255,255,0.35)' : '#ffffff',
                        boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.05)'
                      }}>
                        {getRankLabel(index + 1)}
                      </div>
                      <div style={{ fontWeight: 'bold', fontSize: '1rem' }}>{item.name}</div>
                    </div>
                    <div style={{ fontWeight: 'bold', fontSize: '1rem' }}>{item.accuracy}%</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div style={{
          flex: '0.9',
          minWidth: '250px',
          backgroundColor: 'white',
          borderRadius: '16px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
          border: '1px solid #e9ecef',
          overflow: 'hidden'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '0.85rem 1rem',
            background: 'linear-gradient(135deg, #f5f7fa, #e9eef5)',
            color: '#2c3e50',
            borderBottom: '1px solid #e9ecef'
          }}>
            <h3 style={{ margin: 0, fontSize: '20px' }}>
              {language === 'hi' ? 'सूचनाएं' : 'Notifications'}
            </h3>
            <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#5a67d8' }}>
              {notifications.length}
            </span>
          </div>

          <div style={{ padding: '0.7rem' }}>
            {notifications.length === 0 ? (
              <div style={{ textAlign: 'center', color: '#6c757d', padding: '0.75rem 0', fontSize: '13px' }}>
                {language === 'hi' ? 'कोई सूचना नहीं है।' : 'No notifications yet.'}
              </div>
            ) : (
              <div style={{ display: 'grid', gap: '0.5rem' }}>
                {notifications.map((notif) => (
                  <div
                    key={notif.id}
                    style={{
                      padding: '0.6rem 0.7rem',
                      borderRadius: '10px',
                      backgroundColor: notif.type === 'premium' ? '#f3e8ff' : '#f8f9fa',
                      border: '1px solid #edf2f7',
                    }}
                  >
                    <div style={{ fontWeight: 'bold', fontSize: '12px', marginBottom: '0.2rem', color: '#2c3e50' }}>
                      {notif.title}
                    </div>
                    <div style={{ fontSize: '12px', color: '#4b5563', lineHeight: '1.4' }}>
                      {notif.content}
                    </div>
                    <div style={{ fontSize: '11px', color: '#6c757d', marginTop: '0.35rem' }}>
                      {new Date(notif.date).toLocaleDateString()}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div style={{
        display: 'flex',
        gap: '3rem',
        marginBottom: '3rem',
        flexWrap: 'wrap'
      }}>
        {/* Left Panel - Subscription Information */}
        <div style={{
          flex: '1',
          minHeight: '500px',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: '20px',
          boxShadow: '0 12px 40px rgba(102, 126, 234, 0.3)',
          color: 'white',
          padding: '1.5rem',
          transition: 'transform 0.3s ease, box-shadow 0.3s ease',
          fontFamily: language === 'hi' ? 'KrutiDev_010, serif' : 'Arial, sans-serif',
          fontSize: '18px',
          lineHeight: '1.8'
        }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'translateY(-5px)';
            e.target.style.boxShadow = '0 16px 48px rgba(102, 126, 234, 0.4)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 12px 40px rgba(102, 126, 234, 0.3)';
          }}
        >
          <h2 style={{
            fontSize: '28px',
            marginBottom: '1.5rem',
            textAlign: 'center',
            borderBottom: '2px solid rgba(255,255,255,0.3)',
            paddingBottom: '1rem'
          }}>
            {language === 'hi' ? 'टेस्ट प्लान' : 'Test Plan'}
          </h2>

          <div style={{ marginBottom: '2rem' }}>
            <p style={{ marginBottom: '1rem' }}>
              {language === 'hi' ? 'shorthandlover.com साईट पर रजिस्ट्रेशनकर्ता को कुछ टेस्ट फ्री में उपलब्ध होंगे। सभी टेस्ट चालू करने के लिए प्रीमियम प्लान खरीदना होगा जो कि अत्यधिक कम शुल्क में उपलब्ध है।' : 'Registered users on shorthandlover.com will have some tests available for free. To activate all tests, you will need to purchase the premium plan, which is available at an extremely low cost.'}
            </p>
          </div>

          <h3 style={{
            fontSize: '22px',
            marginBottom: '1.5rem',
            textAlign: 'center'
          }}>
            {language === 'hi' ? 'How to purchase' : 'How to Purchase'}
          </h3>

          <div style={{ marginBottom: '2rem' }}>
            <p style={{ marginBottom: '1rem' }}>
              {language === 'hi' ? 'shorthandlover.com की सदस्यता लेने के लिए --' : 'To subscribe to shorthandlover.com --'}
            </p>
            <ol style={{ paddingLeft: '1.5rem', marginBottom: '1.5rem' }}>
              <li style={{ marginBottom: '1rem' }}>
                {language === 'hi' ? 'हेल्पलाईन व्हाट्स एप नंबर 7597838305 पर मैसेज करें] प्लान के बारे में जानें और उनके द्वारा दिए गए QR CODE पर किसी भी ऑनलाईन पेमेन्ट एप से पेमेन्ट करके स्क्रीनशॉट भेजें।' : 'Message on helpline WhatsApp number 7597838305 to learn about the plan and send a screenshot of the payment made through any online payment app on the QR CODE provided by them.'}
              </li>
              <li>
                {language === 'hi' ? 'पेमेन्ट करने के बाद आपका प्रीमियम प्लान एक्टिव हो जाएगा] उसके बाद आप सभी टेस्टों का लाभ ले सकते हैं' : 'After payment, your premium plan will be activated, and then you can take advantage of all tests.'}
              </li>
            </ol>
          </div>

          <div style={{
            textAlign: 'center',
            padding: '1rem',
            backgroundColor: 'rgba(255,255,255,0.1)',
            borderRadius: '10px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}>
            <img
              src="/shorthandlover.png"
              alt="Steno Logo"
              style={{
                width: '200px',
                height: '200px',
                objectFit: 'contain',
                marginBottom: '0.5rem',
                borderRadius: '50%',
                border: '2px solid rgba(255,255,255,0.3)'
              }}
            />
            {/* <p style={{ marginTop: '0.5rem', fontSize: '14px' }}>वास्तविक QR कोड प्राप्त करने के लिए हमसे संपर्क करें</p> */}
          </div>
          <h2 style={{
            fontSize: '28px',
            marginBottom: '1.5rem',
            textAlign: 'center',
            borderBottom: '2px solid rgba(255,255,255,0.3)',
            paddingBottom: '1rem'
          }}>
            {language === 'hi' ? 'टेस्ट देने के लिए दिशानिर्देश-' : 'Guidelines for taking the test-'}
          </h2>
          <ol style={{ paddingLeft: '1.5rem', marginBottom: '1.5rem' }}>
            <li style={{ marginBottom: '1rem' }}>
              {language === 'hi' ? 'टेस्ट समय पर पूर्ण नहीं होने पर स्वतः ही सबमिट हो जाएगा।' : 'If the test is not completed on time, it will be submitted automatically.'}
            </li>
            <li style={{ marginBottom: '1rem' }}>
              {language === 'hi' ? 'निम्न शब्दों के लिए दिए गए कोड का उपयोग करें।' : 'Use the following codes for the words below.'}
              <ul style={{ marginTop: '0.5rem', marginLeft: '1.5rem' }}>
                <li>{language === 'hi' ? 'कृ - Alt + 0209 ( उदाहरण – कृत्रिम )' : 'कृ - Alt + 0209 (Example – Artificial)'}</li>
                <li>{language === 'hi' ? 'त्त् - Alt + 0217 ( उदाहरण – सत्ता )' : 'त्त् - Alt + 0217 (Example – Authority)'}</li>
                <li>{language === 'hi' ? 'क्र - Alt + 0216 ( उदाहरण – क्रमांक )' : 'क्र - Alt + 0216 (Example – Sequence number)'}</li>
                <li>{language === 'hi' ? 'फ् - Alt + 0182 ( उदाहरण – गिरफ्तार )' : 'फ् - Alt + 0182 (Example – Arrested)'}</li>
              </ul>
            </li>
            <li style={{ marginBottom: '1rem' }}>
              {language === 'hi' ? 'शब्दों में आधा म और आधा न की जगह अनुस्वार/बिन्दु का प्रयोग करें। अतिआवश्यक होने पर ही आधा न और आधा म का प्रयोग करें' : 'Instead of half m and half n in words, use anuswar/bindhu. Use half n and half m only when absolutely necessary.'}
            </li>
          </ol>
        </div>

        {/* Right Panel - Hindi Features Tile */}
        <div style={{
          flex: '1',
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '20px',
          boxShadow: '0 12px 40px rgba(0,0,0,0.1)',
          border: '1px solid #e9ecef',
          fontFamily: language === 'hi' ? 'KrutiDev_010, serif' : 'Arial, sans-serif',
          fontSize: '16px',
          lineHeight: '1.8',
          transition: 'transform 0.3s ease, box-shadow 0.3s ease'
        }}
          onMouseEnter={(e) => {
            e.target.style.transform = 'translateY(-2px)';
            e.target.style.boxShadow = '0 16px 48px rgba(0,0,0,0.15)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = 'translateY(0)';
            e.target.style.boxShadow = '0 12px 40px rgba(0,0,0,0.1)';
          }}
        >
          <h2 style={{
            fontFamily: language === 'hi' ? 'KrutiDev_010, serif' : 'Arial, sans-serif',
            fontSize: '32px',
            marginBottom: '2rem',
            color: '#2c3e50',
            borderBottom: '3px solid #667eea',
            paddingBottom: '1rem',
            textAlign: 'center'
          }}>{language === 'hi' ? 'विशेषताएं' : 'Features'}</h2>
          <ol style={{ paddingLeft: '0', listStylePosition: 'inside' }}>
            {[
              language === 'hi' ? 'सभी टेस्ट Krutidev010 Font में उपलब्ध हैं।' : 'All tests are available in Krutidev010 Font.',
              language === 'hi' ? 'सभी टेस्टों में मानक शुद्ध हिन्दी के प्रमाणित शब्दों का प्रयोग किया गया है।' : 'Standard pure Hindi certified words are used in all tests.',
              language === 'hi' ? 'कोमा, विराम, अल्पविराम, योजक चिह्न.... इत्यादि का शुद्ध हिन्दी के नियमों के अनुसार प्रयोग किया गया है।' : 'Comma, full stop, semicolon, conjunction signs, etc. are used according to the rules of pure Hindi.',
              language === 'hi' ? 'टेस्ट सबमिट करने के बाद रिजल्ट में आपके द्वारा गलत किए गए शब्द रेड मार्क में दिखाई देते हैं तथा परीक्षा प्रणाली के अनुसार ही आपके नंबर भी दर्शित होते हैं।' : 'After submitting the test, the words you got wrong are shown in red mark in the result, and your scores are displayed according to the examination system.',
              language === 'hi' ? 'सभी टेस्टों में सलेक्टेड स्टेनो एक्सपर्टस की राय लेकर ही शब्दों का प्रयोग किया गया है।' : 'Words are used in all tests only after taking the opinion of selected steno experts.',
              language === 'hi' ? 'Steno Test की टेब में राईट टॉप पर Search का बटन है, जिस पर अपने एक्जाम का नाम या स्पीड लिखकर, उसी तरह की डिक्टेशन का टेस्ट दे सकते है।' : 'In the Steno Test table, there is a Search button on the right top, by writing your exam name or speed on it, you can take a test of that kind of dictation.',
              language === 'hi' ? 'इस साईट पर All India में आयोजित होने वाली परीक्षाओं के अनुसार डिक्टेशन अपलोड की गई हैं। आप जिसकी तैयारी कर रहे हैं, उसी प्रकार का टेस्ट देकर अपना आकलन कर सकते हैं।' : 'Dictations have been uploaded on this site according to the exams conducted all over India. By taking the test of the type you are preparing for, you can assess yourself.',
              language === 'hi' ? 'इस टेस्ट सिरीज की फीस नाम-मात्र रखी गई है, जिसे हर आम विद्यार्थी के लिए सुलभ है और अन्य सभी स्टेनो साईट से कम है।' : 'The fee for this test series is nominal, which is affordable for every common student and less than all other steno sites.'
            ].map((item, index) => (
              <li key={index} style={{
                marginBottom: '1.5rem',
                padding: '1rem',
                backgroundColor: '#f8f9fa',
                borderRadius: '12px',
                borderLeft: '4px solid #667eea',
                boxShadow: '0 2px 8px rgba(102, 126, 234, 0.1)',
                transition: 'all 0.3s ease'
              }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#e3f2fd';
                  e.target.style.borderLeft = '4px solid #5a67d8';
                  e.target.style.transform = 'translateX(5px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#f8f9fa';
                  e.target.style.borderLeft = '4px solid #667eea';
                  e.target.style.transform = 'translateX(0)';
                }}
              >
                {item}
              </li>
            ))}
          </ol>
        </div>
      </div>


    </div>
  );
}

export default Home;
